/**
 * DefaultsTab - Iter 9.40 Pass C dynamic schema rewire
 *
 * BEFORE: 2,310 lines with hardcoded `DEFAULT_CATEGORIES` map (~1,400 lines of
 *         static option lists for every category × every field).
 * AFTER:  Renders categories + fields fetched from `GET /api/me/preferences/schema`
 *         which is sourced from `db.categories.preference_fields` +
 *         `db.master_intakes.universal_defaults`. Zero hardcoded options -
 *         admins control the whole schema.
 *
 * Parent contract preserved (UserProfilePage MobileDefaultsTab):
 *   - props: { userDefaults, onSaveCategory(catId, data), onClearCategory(catId), userPilots }
 *   - userDefaults shape: { defaults: { [catId]: data }, filled_categories: [...] }
 *   - onSaveCategory body: arbitrary object stored directly under user_preferences.<catId>
 */
import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles, Plane, Check, X, Trash2, ChevronRight, Plus, Shield,
  Utensils, Accessibility, Heart, Image as ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Admins decide group placement via category_id. Only category_id used for grouping.
const BEAUTY_IDS = new Set(["makeup", "hair", "lashes", "nails"]);
const UNIVERSAL_BUCKETS = ["allergies", "accessibility", "dietary"];
const INTERESTS_KEY = "interests_lifestyle"; // Iter 9.40 Pass E - pseudo-bucket id

const UNIVERSAL_META = {
  allergies:           { icon: Shield,        accent: "bg-red-100 text-red-600",       border: "border-red-200" },
  accessibility:       { icon: Accessibility, accent: "bg-blue-100 text-blue-600",     border: "border-blue-200" },
  dietary:             { icon: Utensils,      accent: "bg-orange-100 text-orange-600", border: "border-orange-200" },
  interests_lifestyle: { icon: Heart,         accent: "bg-fuchsia-100 text-fuchsia-600", border: "border-fuchsia-200" },
};

// ---------------------------------------------------------------------------
// Schema fetch hook
// ---------------------------------------------------------------------------
const usePreferenceSchema = () => {
  const [schema, setSchema] = useState(null);
  const [interests, setInterests] = useState({ tags: [], options: [] }); // Iter 9.40 Pass E
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetchInterests = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/me/interests/lifestyle`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) setInterests(await res.json());
    } catch { /* silent */ }
  };

  useEffect(() => {
    const run = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const [schemaRes, interestsRes] = await Promise.all([
          fetch(`${API}/me/preferences/schema`, { headers }),
          fetch(`${API}/me/interests/lifestyle`, { headers }),
        ]);
        if (!schemaRes.ok) throw new Error(`Schema fetch failed (${schemaRes.status})`);
        const data = await schemaRes.json();
        setSchema(data);
        if (interestsRes.ok) setInterests(await interestsRes.json());
      } catch (e) {
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  return { schema, interests, refetchInterests, loading, error };
};

// ---------------------------------------------------------------------------
// CollapsibleSection - Pass 4g/4h - every section is now collapsible. By
// default sections are OPEN. Sections with `collapsible_section: true` on
// any of their fields (e.g. Makeup Classes) start CLOSED.
// ---------------------------------------------------------------------------
const CollapsibleSection = ({ section, renderField }) => {
  const startsClosed = section.fields.some((f) => f.collapsible_section);
  const [open, setOpen] = useState(!startsClosed);
  const labeled = Boolean(section.section_label);
  return (
    <div className="space-y-4" data-testid={`pref-section-${section.section_id}`}>
      {labeled && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          data-testid={`pref-section-toggle-${section.section_id}`}
          className="flex items-center gap-2 pt-1 w-full text-left group"
        >
          <ChevronRight
            className={`w-3.5 h-3.5 text-gray-500 transition-transform group-hover:text-gray-900 ${
              open ? "rotate-90" : ""
            }`}
          />
          <h4 className="text-[11px] font-semibold tracking-wider uppercase text-gray-700 group-hover:text-gray-900">
            {section.section_label}
          </h4>
          {!open && (
            <span className="text-[10px] text-gray-400">
              ({section.fields.length})
            </span>
          )}
          <div className="flex-1 h-px bg-gray-200" />
        </button>
      )}
      {open && (
        <div className="space-y-5">
          {section.fields.map((field) => renderField(field))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Multi-select Options (replaces legacy term "chip"). Supports image_url,
// swatch_color (Pass 3 visual metadata) and tooltip-on-hover.
// ---------------------------------------------------------------------------
const MultiSelectOptions = ({ field, value = [], onChange }) => {
  const normalized = Array.isArray(value) ? value : value ? [value] : [];
  const toggle = (id) => {
    if (normalized.includes(id)) {
      onChange(normalized.filter((v) => v !== id));
    } else {
      onChange([...normalized, id]);
    }
  };
  const enabled = (field.options || []).filter((o) => o.enabled !== false);
  const hasImages = enabled.some((o) => o.image_url);
  const hasSwatches = !hasImages && enabled.some((o) => o.swatch_color);

  return (
    <div className="space-y-2" data-testid={`pref-field-${field.key}`}>
      <label className="text-sm font-medium text-gray-700">{field.label}</label>
      {field.help && <p className="text-xs text-gray-500">{field.help}</p>}
      {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
      <div className={hasImages ? "grid grid-cols-3 gap-2" : "flex flex-wrap gap-2"}>
        {enabled.map((opt) => {
          const selected = normalized.includes(opt.id);
          if (hasImages) {
            const isNone = String(opt.id).toLowerCase() === "none";
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                title={opt.tooltip || opt.label}
                data-testid={`pref-option-${field.key}-${opt.id}`}
                className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                  selected ? "border-gray-900 shadow-md" : "border-gray-200 hover:border-gray-400"
                }`}
              >
                <div
                  className="aspect-square flex items-center justify-center"
                  style={
                    isNone
                      ? { background: "#f9fafb" }
                      : opt.swatch_color?.startsWith("linear-gradient")
                      ? { backgroundImage: opt.swatch_color }
                      : opt.swatch_color
                      ? { background: opt.swatch_color }
                      : { background: "#f3f4f6" }
                  }
                >
                  {isNone ? (
                    <X className="w-8 h-8 text-gray-400" strokeWidth={2} />
                  ) : opt.image_url ? (
                    <img
                      src={opt.image_url}
                      alt={opt.label}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : !opt.swatch_color ? <ImageIcon className="w-6 h-6 text-gray-400" /> : null}
                </div>
                <div className="px-2 py-1.5 text-xs font-medium text-gray-900 text-left">
                  {opt.label}
                </div>
                {selected && (
                  <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            );
          }
          if (hasSwatches && opt.swatch_color) {
            const isGradient = opt.swatch_color.startsWith("linear-gradient");
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                title={opt.tooltip || opt.label}
                data-testid={`pref-option-${field.key}-${opt.id}`}
                className={`flex items-center gap-2 pl-1.5 pr-3 py-1 rounded-full text-sm font-medium border-2 transition-colors ${
                  selected ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
                }`}
              >
                <span
                  className="w-5 h-5 rounded-full border border-gray-300 flex-shrink-0"
                  style={isGradient ? { backgroundImage: opt.swatch_color } : { background: opt.swatch_color }}
                />
                {opt.label}
                {selected && <Check className="w-3 h-3 ml-1" />}
              </button>
            );
          }
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => toggle(opt.id)}
              title={opt.tooltip || opt.label}
              data-testid={`pref-option-${field.key}-${opt.id}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selected
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {opt.label}
              {selected && <Check className="w-3 h-3 inline ml-1" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Searchable Master Picker - Pass 4d - opens a popup with type-filter options
// + grid of product cards (brand logo + product image + name + shade).
// Used for foundation_shade, lash_strip_brand, favorite_products, favorite_brands.
//
// Pass 4d-2 - connected filtering: when the user has already picked a generic
// shade (`foundation_shade_generic`) we pre-filter results to that shade_group,
// and selecting a product autofills the matching shade_group back into the
// generic field.
// ---------------------------------------------------------------------------
const GENERIC_SHADE_SWATCHES = {
  "Fair": "#F5DBC3", "Light": "#EFC5A6", "Light-Medium": "#DFB089",
  "Medium": "#C99973", "Medium-Tan": "#B7825D", "Tan": "#9A6749",
  "Deep": "#6F4632", "Rich": "#4A2D20",
};

const SearchableMasterPicker = ({ field, value = [], onChange, formData = {}, onConnectedAutofill }) => {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState(field.filter?.product_group || "All");
  const [shadeFilter, setShadeFilter] = useState("All");
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  // Connected filter - prefill shade filter from a previously chosen generic shade
  useEffect(() => {
    if (!open) return;
    const generic = formData?.foundation_shade_generic;
    if (Array.isArray(generic) && generic.length === 1) setShadeFilter(generic[0]);
  }, [open, formData?.foundation_shade_generic]);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const isBrands = field.master_source === "master_brands";
        const baseFilter = { ...(field.filter || {}) };
        delete baseFilter.product_group;
        const params = new URLSearchParams(baseFilter);
        const path = isBrands ? "master-brands" : "master-products";
        const res = await fetch(`${API}/${path}?${params}&limit=500`, { headers });
        if (!res.ok) throw new Error();
        const d = await res.json();
        if (!cancel) setItems(d.products || d.brands || d.items || (Array.isArray(d) ? d : []));
      } catch { if (!cancel) setItems([]); }
    })();
  }, [field.master_source, JSON.stringify(field.filter || {}), open]);

  const itemId = (it) => it.product_id || it.master_product_id || it.brand_id || it.id;
  const itemLabel = (it) => it.brand && it.name
    ? `${it.brand} · ${it.name}${it.shade_code ? " · " + it.shade_code : ""}`
    : (it.name || it.shade || itemId(it));

  const isBrandsPicker = field.master_source === "master_brands";
  // Pass 4h - when the field is locked to a single product_group (e.g. Lip
  // Liner picker only ever shows Lip Liner), hide the Type filter options
  // entirely. Only multi-type pickers like favorite_products show them.
  const isTypeLocked = Boolean(field.filter?.product_group);

  const availableTypes = useMemo(() => {
    // Pass 4g - show every type_filter the admin configured for this picker,
    // not just types we happen to have items for. Lets users see the full
    // taxonomy (Foundation, Concealer, Primer, Contour, etc.) and discover
    // even when results are still being curated.
    if (field.type_filters && field.type_filters.length) {
      return ["All", ...field.type_filters];
    }
    const set = new Set(items.map((it) => it.product_group).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [items, field.type_filters]);

  const showTypeFilters = !isBrandsPicker && !isTypeLocked && availableTypes.length > 2;

  const availableShades = useMemo(() => {
    // Pass 4g - only surface SHADE filter when Foundation is active type
    // (or any product_group that carries shades - Foundation/Blush/Lip).
    const SHADED = new Set(["Foundation", "Blush", "Lipstick", "Lip Gloss", "Lip Liner"]);
    if (typeFilter === "All" || !SHADED.has(typeFilter)) return [];
    const set = new Set(items.filter((it) => it.product_group === typeFilter).map((it) => it.shade_group).filter(Boolean));
    return ["All", ...Object.keys(GENERIC_SHADE_SWATCHES).filter((s) => set.has(s))];
  }, [items, typeFilter]);

  const filtered = useMemo(() => {
    let list = items;
    if (typeFilter !== "All") list = list.filter((it) => it.product_group === typeFilter);
    if (shadeFilter !== "All") list = list.filter((it) => it.shade_group === shadeFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((it) => itemLabel(it).toLowerCase().includes(q));
    }
    return list;
  }, [items, typeFilter, shadeFilter, query]);

  // Group results by product_group → shade_group when no top-level filter applied.
  // For brands picker, no grouping (just a flat grid of logo + name cards).
  const grouped = useMemo(() => {
    if (isBrandsPicker) return [{ group: null, subgroups: [{ shade: null, items: filtered }] }];
    const byGroup = {};
    filtered.forEach((it) => {
      const g = it.product_group || "Other";
      if (!byGroup[g]) byGroup[g] = {};
      const sg = g === "Foundation" ? (it.shade_group || "Other") : null;
      const key = sg || "_all";
      if (!byGroup[g][key]) byGroup[g][key] = [];
      byGroup[g][key].push(it);
    });
    // Stable order - Foundation first when present
    const order = (field.type_filters || Object.keys(byGroup)).filter((g) => g in byGroup);
    Object.keys(byGroup).forEach((g) => { if (!order.includes(g)) order.push(g); });
    return order.map((g) => ({
      group: g,
      subgroups: Object.entries(byGroup[g]).map(([shade, list]) => ({
        shade: shade === "_all" ? null : shade,
        items: list,
      })),
    }));
  }, [filtered, isBrandsPicker, field.type_filters]);

  const linked = useMemo(
    () => items.filter((it) => selected.includes(itemId(it))),
    [items, selected]
  );

  const toggle = (it) => {
    const id = itemId(it);
    const isAdding = !selected.includes(id);
    const next = isAdding ? [...selected, id] : selected.filter((x) => x !== id);
    onChange(next);
    if (isAdding && it.shade_group && onConnectedAutofill) {
      onConnectedAutofill("foundation_shade_generic", [it.shade_group]);
    }
    // Pass 4h - every product picked in a specific picker also lands in the
    // unified favorite_products field, AND the brand auto-links into
    // favorite_brands. Selecting a product in favorite_products itself
    // auto-links the brand only.
    if (isAdding && onConnectedAutofill && !isBrandsPicker) {
      if (field.key !== "favorite_products") {
        const existingFavs = formData?.favorite_products || [];
        if (!existingFavs.includes(id)) {
          onConnectedAutofill("favorite_products", [...existingFavs, id]);
        }
      }
      if (it.brand) {
        const brandId = (it.brand || "").toLowerCase().replace(/\s+/g, "_");
        const existingBrands = formData?.favorite_brands || [];
        if (!existingBrands.includes(brandId) && !existingBrands.includes(it.brand)) {
          onConnectedAutofill("favorite_brands", [...existingBrands, brandId]);
        }
      }
    }
  };

  return (
    <div className="space-y-2" data-testid={`pref-search-${field.key}`}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`pref-search-toggle-${field.key}`}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-gray-400 transition-colors"
      >
        <div className="text-left">
          <div className="text-sm font-medium text-gray-700">{field.label}</div>
          <div className="text-xs text-gray-500">
            {linked.length === 0 ? "Tap to search and link" : `${linked.length} linked`}
          </div>
        </div>
        <Plus className="w-4 h-4 text-gray-500" />
      </button>

      {linked.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {linked.map((it) => (
            <span
              key={itemId(it)}
              className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 text-xs bg-gray-900 text-white rounded-full"
              data-testid={`pref-linked-${itemId(it)}`}
            >
              {it.brand_logo_url && (
                <span className="w-4 h-4 rounded-full bg-white p-0.5 overflow-hidden flex items-center justify-center">
                  <img src={it.brand_logo_url} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                </span>
              )}
              {itemLabel(it)}
              <button onClick={() => toggle(it)} className="hover:text-red-300" type="button">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {open && (
        <Dialog open={true} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid={`pref-search-dialog-${field.key}`}>
            <DialogHeader>
              <DialogTitle>{field.label}</DialogTitle>
              {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
            </DialogHeader>

            <div className="relative">
              <input
                type="text"
                placeholder="Search by brand or product name…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid={`pref-search-input-${field.key}`}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-black"
              />
              <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>

            {/* Type filter options - hidden when the field is locked to a single product_group */}
            {showTypeFilters && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Type</div>
                <div className="flex flex-wrap gap-1.5">
                  {availableTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTypeFilter(t)}
                      data-testid={`pref-search-type-${field.key}-${t}`}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        typeFilter === t
                          ? "bg-gray-900 text-white border-gray-900"
                          : "bg-white text-gray-700 border-gray-300 hover:border-gray-900"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Shade filter options - only for product picker, not brands */}
            {!isBrandsPicker && availableShades.length > 1 && (
              <div>
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Shade</div>
                <div className="flex flex-wrap gap-1.5">
                  {availableShades.map((s) => {
                    const swatch = GENERIC_SHADE_SWATCHES[s];
                    const active = shadeFilter === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setShadeFilter(s)}
                        data-testid={`pref-search-shade-${field.key}-${s}`}
                        className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 text-xs rounded-full border transition-colors ${
                          active ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-700 border-gray-300 hover:border-gray-900"
                        }`}
                      >
                        {swatch && <span className="w-4 h-4 rounded-full border border-gray-300" style={{ background: swatch }} />}
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Result grid - grouped by product_group → shade_group */}
            <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-4">
              {filtered.length === 0 ? (
                <div className="text-center py-12 text-sm text-gray-500">
                  No matches yet. Try a different search.
                </div>
              ) : isBrandsPicker ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 py-2">
                  {filtered.map((it) => {
                    const id = itemId(it);
                    const active = selected.includes(id);
                    return (
                      <button
                        type="button"
                        key={id}
                        onClick={() => toggle(it)}
                        data-testid={`pref-search-result-${id}`}
                        className={`relative flex flex-col items-center justify-center gap-2 p-3 rounded-xl border-2 text-center transition-all ${
                          active ? "border-gray-900 shadow-md bg-gray-50" : "border-gray-200 hover:border-gray-400 bg-white"
                        }`}
                      >
                        <div className="w-16 h-16 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden">
                          {it.logo_url ? (
                            <img src={it.logo_url} alt={it.name} className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                          ) : (
                            <span className="text-xl font-bold text-gray-400">{it.name?.[0] || "?"}</span>
                          )}
                        </div>
                        <div className="text-xs font-semibold text-gray-900">{it.name}</div>
                        {active && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                grouped.map(({ group, subgroups }) => {
                  if (subgroups.every((s) => s.items.length === 0)) return null;
                  return (
                    <div key={group || "all"} data-testid={`pref-search-group-${group}`}>
                      {group && (
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-[11px] font-semibold tracking-wider uppercase text-gray-700">{group}</h4>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>
                      )}
                      {subgroups.map(({ shade, items: list }) => (
                        <div key={shade || "all"} className="mb-3">
                          {shade && (
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <span className="w-3 h-3 rounded-full border border-gray-200" style={{ background: GENERIC_SHADE_SWATCHES[shade] || "#e5e7eb" }} />
                              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">{shade}</span>
                            </div>
                          )}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {list.map((it) => {
                              const id = itemId(it);
                              const active = selected.includes(id);
                              const swatch = GENERIC_SHADE_SWATCHES[it.shade_group];
                              return (
                                <button
                                  type="button"
                                  key={id}
                                  onClick={() => toggle(it)}
                                  data-testid={`pref-search-result-${id}`}
                                  className={`relative flex flex-col items-stretch gap-1.5 p-2 rounded-xl border-2 text-left transition-all ${
                                    active ? "border-gray-900 shadow-md bg-gray-50" : "border-gray-200 hover:border-gray-400 bg-white"
                                  }`}
                                >
                                  <div className="flex items-center gap-2 w-full">
                                    {it.brand_logo_url ? (
                                      <div className="w-7 h-7 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                        <img src={it.brand_logo_url} alt="" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                      </div>
                                    ) : (
                                      <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-700 flex-shrink-0">
                                        {it.brand?.[0] || "?"}
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[10px] font-medium text-gray-500 truncate">{it.brand}</div>
                                    </div>
                                    {active && <Check className="w-4 h-4 text-green-500 flex-shrink-0" />}
                                  </div>
                                  <ProductVisual it={it} swatch={swatch} />
                                  <div className="text-xs font-semibold text-gray-900 line-clamp-2">{it.name || it.shade}</div>
                                  {it.shade_code && (
                                    <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
                                      {swatch && <span className="w-3 h-3 rounded-full border border-gray-200" style={{ background: swatch }} />}
                                      Shade {it.shade_code}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex justify-between pt-3 border-t">
              <span className="text-xs text-gray-500 self-center">
                {selected.length} selected · {filtered.length} match{filtered.length !== 1 ? "es" : ""}
              </span>
              <Button onClick={() => setOpen(false)} data-testid={`pref-search-done-${field.key}`}>
                Done
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

// Product visual that gracefully falls back to a swatch tile if image fails
const ProductVisual = ({ it, swatch }) => {
  const [ok, setOk] = useState(Boolean(it.image_url));
  if (!it.image_url || !ok) {
    return (
      <div
        className="w-full aspect-square rounded-lg flex items-center justify-center"
        style={swatch ? { background: swatch } : { background: "#f3f4f6" }}
      >
        {!swatch && <ImageIcon className="w-6 h-6 text-gray-300" />}
      </div>
    );
  }
  return (
    <div className="w-full aspect-square bg-gray-100 rounded-lg overflow-hidden">
      <img
        src={it.image_url}
        alt={it.name}
        className="w-full h-full object-cover"
        onError={() => setOk(false)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Master Picker - Pass 4: collapses many shades of the same product into a
// single product row with a shade dropdown, and surfaces brand_logo/image_url.
// ---------------------------------------------------------------------------
const MasterPickerField = ({ field, value = [], onChange }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const isBrands = field.master_source === "master_brands";
        const params = new URLSearchParams(field.filter || {});
        const path = isBrands ? "master-brands" : "master-products";
        const res = await fetch(`${API}/${path}?${params}`, { headers });
        if (!res.ok) throw new Error(`fetch ${path} failed`);
        const d = await res.json();
        if (!cancel) setItems(d.products || d.brands || d.items || (Array.isArray(d) ? d : []));
      } catch {
        if (!cancel) setItems([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
  }, [field.master_source, JSON.stringify(field.filter || {})]);

  // Group by (brand, name) so multiple shades of the same product collapse
  // into ONE row with a shade dropdown.
  const productRows = useMemo(() => {
    const rows = {};
    items.forEach((it) => {
      const key = `${it.brand || ""}__${it.name || it.id}`;
      if (!rows[key]) {
        rows[key] = {
          key,
          brand: it.brand,
          name: it.name || it.shade || key,
          brand_logo_url: it.brand_logo_url,
          image_url: it.image_url,
          shades: [],
        };
      }
      rows[key].shades.push({
        id: it.product_id || it.master_product_id || it.id,
        label: it.shade_code || it.shade || it.name,
        shade_group: it.shade_group,
      });
    });
    return Object.values(rows);
  }, [items]);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((v) => v !== id));
    else onChange([...selected, id]);
  };

  return (
    <div className="space-y-3" data-testid={`pref-field-${field.key}`}>
      <label className="text-sm font-medium text-gray-700">{field.label}</label>
      {field.help_text && <p className="text-xs text-gray-500">{field.help_text}</p>}
      {loading && <div className="text-xs text-gray-400">Loading products…</div>}
      {!loading && productRows.length === 0 && (
        <div className="text-xs text-gray-500 italic">
          No matching products yet - your host will publish their go-to options soon.
        </div>
      )}
      <div className="space-y-2">
        {productRows.map((row) => {
          const activeShade = row.shades.find((s) => selected.includes(s.id));
          const hasMulti = row.shades.length > 1;
          return (
            <div
              key={row.key}
              data-testid={`pref-product-row-${row.key.replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 p-2.5 rounded-xl border-2 transition-colors ${
                activeShade ? "border-gray-900 bg-gray-50" : "border-gray-200 hover:border-gray-400"
              }`}
            >
              {/* Brand logo */}
              <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                {row.brand_logo_url ? (
                  <img src={row.brand_logo_url} alt={row.brand} className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <span className="text-[10px] font-semibold text-gray-700">{row.brand?.[0] || "?"}</span>
                )}
              </div>
              {/* Product image */}
              <div className="w-12 h-12 rounded-lg bg-stone-100 overflow-hidden flex-shrink-0">
                {row.image_url && (
                  <img src={row.image_url} alt={row.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
              </div>
              {/* Brand + product name */}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-500">{row.brand}</div>
                <div className="text-sm font-semibold text-gray-900 truncate">{row.name}</div>
              </div>
              {/* Shade selector */}
              {hasMulti ? (
                <select
                  value={activeShade?.id || ""}
                  onChange={(e) => {
                    // remove any prior shade of this product, add the new one
                    const productIds = row.shades.map((s) => s.id);
                    const next = selected.filter((id) => !productIds.includes(id));
                    if (e.target.value) next.push(e.target.value);
                    onChange(next);
                  }}
                  data-testid={`pref-shade-select-${row.key.replace(/\s+/g, "-")}`}
                  className="text-xs px-2 py-1.5 border border-gray-300 rounded-lg bg-white max-w-[120px]"
                >
                  <option value="">Pick shade…</option>
                  {row.shades.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              ) : (
                <button
                  type="button"
                  onClick={() => toggle(row.shades[0].id)}
                  data-testid={`pref-shade-toggle-${row.key.replace(/\s+/g, "-")}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    selected.includes(row.shades[0].id)
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-700 border-gray-300 hover:border-gray-900"
                  }`}
                >
                  {row.shades[0].label || "Add"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Category Card - universal bucket OR per-category preview
// ---------------------------------------------------------------------------
export const CategoryCard = ({
  id,
  label,
  description,
  icon: Icon,
  iconUrl,           // Pass 4b - admin-driven category icon
  accent,
  border,
  data,
  fields,
  onClick,
  onClear,
}) => {
  const isFilled = data && Object.keys(data || {}).some((k) => {
    const v = data[k];
    return Array.isArray(v) ? v.length > 0 : Boolean(v);
  });

  // Build a short preview list of selected option labels
  const summary = useMemo(() => {
    if (!data || !fields) return [];
    const items = [];
    for (const f of fields) {
      const v = data[f.key];
      if (Array.isArray(v) && v.length) {
        const labels = v.slice(0, 2).map((id) => {
          const opt = (f.options || []).find((o) => o.id === id);
          return opt?.label || id;
        });
        items.push(...labels);
        if (v.length > 2) items.push(`+${v.length - 2}`);
      }
    }
    return items.slice(0, 4);
  }, [data, fields]);

  return (
    <div className="relative group">
      <button
        type="button"
        onClick={onClick}
        data-testid={`default-card-${id}`}
        className={`w-full p-4 rounded-xl border transition-all text-left ${
          isFilled
            ? `bg-white ${border || "border-gray-200"} hover:shadow-md`
            : "bg-gray-50 border-dashed border-gray-200 hover:border-gray-300 hover:bg-white"
        }`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isFilled ? (accent || "bg-gray-100 text-gray-600") : "bg-gray-100"
          }`}>
            {iconUrl ? (
              <img src={iconUrl} alt="" className={`w-6 h-6 ${isFilled ? "" : "opacity-40"}`} />
            ) : Icon ? (
              <Icon className={`w-5 h-5 ${isFilled ? "" : "text-gray-400"}`} />
            ) : null}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`font-medium ${isFilled ? "text-gray-900" : "text-gray-500"}`}>{label}</span>
              {isFilled && <Check className="w-4 h-4 text-green-500" />}
            </div>
            {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
            {summary.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {summary.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] py-0">{s}</Badge>
                ))}
              </div>
            )}
            {!isFilled && (
              <div className="flex items-center gap-1 mt-2 text-purple-600">
                <Plus className="w-3 h-3" />
                <span className="text-xs font-medium">Set up</span>
              </div>
            )}
          </div>
          <ChevronRight className={`w-5 h-5 flex-shrink-0 ${isFilled ? "text-gray-400" : "text-gray-300"}`} />
        </div>
      </button>
      {isFilled && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Clear your ${label} defaults?`)) onClear(id);
          }}
          data-testid={`default-card-clear-${id}`}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex shadow-md"
          title={`Clear ${label}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Category Edit Modal - schema-driven
// ---------------------------------------------------------------------------
export const CategoryEditModal = ({
  id,
  label,
  icon: Icon,
  accent,
  fields,        // [{key, label, type, options, help}]
  data,          // existing values
  onSave,
  onClose,
  onClear,
}) => {
  const [formData, setFormData] = useState(data || {});

  const updateField = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleSave = () => {
    onSave(id, formData);
    onClose();
  };

  const handleClear = () => {
    if (window.confirm(`Clear all your ${label} defaults?`)) {
      onClear(id);
      onClose();
    }
  };

  // Per spec: category may have AT MOST one notes field. Render last.
  // Pass 4: filter out fields the admin marked as hidden_in_category_modal
  // (universal allergies/accessibility/dietary now live ONLY in universal cards),
  // plus filter conditional fields when their condition is not met.
  const ordered = [...(fields || [])]
    .filter((f) => {
      if (f.hidden_in_category_modal) return false;
      if (f.is_universal && id !== "allergies" && id !== "accessibility" && id !== "dietary") return false;
      // shown_when conditional reveal - for now only support service.is_bridal
      if (f.shown_when && f.shown_when["service.is_bridal"] === true) return false; // hide unless RSVP context flips it
      return true;
    });

  // Pass 4d - group by section so we can render section headers + dividers.
  // Preserve incoming order across sections; notes fields sink to bottom.
  const grouped = useMemo(() => {
    const sections = [];
    const indexBySec = {};
    const isNotes = (t) => t === "notes" || t === "text" || t === "long_text";
    const notesFields = [];
    ordered.forEach((f) => {
      if (isNotes(f.type)) { notesFields.push(f); return; }
      const sid = f.section || "_main";
      const slabel = f.section_label || (sid === "_main" ? "" : sid);
      if (!(sid in indexBySec)) {
        indexBySec[sid] = sections.length;
        sections.push({ section_id: sid, section_label: slabel, fields: [] });
      }
      sections[indexBySec[sid]].fields.push(f);
    });
    if (notesFields.length) sections.push({ section_id: "_notes", section_label: "Notes", fields: notesFields });
    return sections;
  }, [ordered]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid={`pref-modal-${id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent || "bg-gray-100"}`}>
              {Icon ? <Icon className="w-4 h-4" /> : null}
            </div>
            {label} Defaults
          </DialogTitle>
          <p className="text-sm text-gray-500">
            Set your default {label.toLowerCase()} preferences. These auto-fill when you RSVP or plan an experience.
          </p>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1">
          {grouped.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">
              No preference options have been published for this category yet.
            </p>
          )}
          {grouped.map((section) => (
            <CollapsibleSection
              key={section.section_id}
              section={section}
              renderField={(field) => {
                if (field.type === "text" || field.type === "notes" || field.type === "long_text") {
                  return (
                    <div key={field.key} className="space-y-2" data-testid={`pref-field-${field.key}`}>
                      <label className="text-sm font-medium text-gray-700">{field.label}</label>
                      <Textarea
                        value={formData[field.key] || ""}
                        onChange={(e) => updateField(field.key, e.target.value)}
                        placeholder={field.placeholder || "Anything else to share..."}
                        className="min-h-[80px]"
                        data-testid={`pref-notes-${field.key}`}
                      />
                    </div>
                  );
                }
                if (field.type === "multi_select_master") {
                  if (field.picker_mode === "searchable") {
                    return (
                      <SearchableMasterPicker
                        key={field.key}
                        field={field}
                        value={formData[field.key] || []}
                        onChange={(val) => updateField(field.key, val)}
                        formData={formData}
                        onConnectedAutofill={(otherKey, otherVal) => {
                          // Append-mode autofill so cross-syncs (favorite_products,
                          // favorite_brands) merge instead of overwriting.
                          updateField(otherKey, otherVal);
                        }}
                      />
                    );
                  }
                  return (
                    <MasterPickerField
                      key={field.key}
                      field={field}
                      value={formData[field.key] || []}
                      onChange={(val) => updateField(field.key, val)}
                    />
                  );
                }
                return (
                  <MultiSelectOptions
                    key={field.key}
                    field={field}
                    value={formData[field.key] || []}
                    onChange={(val) => updateField(field.key, val)}
                  />
                );
              }}
            />
          ))}
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClear}
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            data-testid={`pref-modal-clear-${id}`}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose} data-testid={`pref-modal-cancel-${id}`}>Cancel</Button>
          <Button onClick={handleSave} data-testid={`pref-modal-save-${id}`}>Save Defaults</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export const DefaultsTab = ({ userDefaults, onSaveCategory, onClearCategory }) => {
  const { schema, interests, refetchInterests, loading, error } = usePreferenceSchema();
  const [editing, setEditing] = useState(null); // {id, label, icon, accent, fields}

  const getData = (id) => {
    if (id === INTERESTS_KEY) return { lifestyle_tags: interests.tags || [] };
    return userDefaults?.defaults?.[id] || userDefaults?.[id] || null;
  };

  // Save handler that special-cases interests (different endpoint).
  const handleSave = async (catId, data) => {
    if (catId === INTERESTS_KEY) {
      try {
        const token = localStorage.getItem("session_token");
        await fetch(`${API}/me/interests/lifestyle`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ tags: data.lifestyle_tags || [] }),
        });
        toast.success("Interests saved");
        await refetchInterests();
      } catch { toast.error("Failed"); }
      return;
    }
    onSaveCategory?.(catId, data);
  };
  const handleClear = async (catId) => {
    if (catId === INTERESTS_KEY) {
      try {
        const token = localStorage.getItem("session_token");
        await fetch(`${API}/me/interests/lifestyle`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ tags: [] }),
        });
        toast.success("Interests cleared");
        await refetchInterests();
      } catch { toast.error("Failed"); }
      return;
    }
    onClearCategory?.(catId);
  };

  // Build universal cards from schema.universal_defaults + Iter 9.40 Pass E interests
  const universalCards = useMemo(() => {
    if (!schema?.universal_defaults) return [];
    const cards = UNIVERSAL_BUCKETS
      .filter((b) => schema.universal_defaults[b])
      .map((b) => {
        const bucket = schema.universal_defaults[b];
        const meta = UNIVERSAL_META[b] || {};
        const optionFieldKey = b === "accessibility" ? "physical_needs"
          : b === "dietary" ? "restrictions"
          : "allergies"; // keep legacy backend keys for /user/preferences/{cat}
        return {
          id: b,
          label: bucket.label || b,
          description: `${bucket.label || b} preferences across all experiences`,
          icon: meta.icon,
          accent: meta.accent,
          border: meta.border,
          // Pass 4 - every universal bucket gets a notes field at the end
          // so users can capture context once and have it sync everywhere.
          fields: [
            {
              key: optionFieldKey,
              label: bucket.label || b,
              type: "multi_select",
              options: bucket.options || [],
            },
            {
              key: "notes",
              label: `${bucket.label || b} notes`,
              type: "long_text",
              placeholder: b === "allergies"
                ? "Severity, EpiPen carrier, cross-contamination concerns…"
                : b === "accessibility"
                ? "Anything else that helps your provider make this comfortable for you…"
                : "Additional dietary context - e.g. low FODMAP, religious observances…",
            },
          ],
        };
      });

    // Iter 9.40 Pass E - Interests & Lifestyle subsection (Pass 4c - hidden until requested)
    // const interestsMeta = UNIVERSAL_META[INTERESTS_KEY];
    // cards.push({...});
    return cards;
  }, [schema, interests]);

  // Per-category cards - split Beauty vs Lifestyle/Other for layout parity
  // Pass 4c - only render categories admins have toggled `show_in_me_defaults`.
  const categoryCards = useMemo(() => {
    if (!schema?.categories) return { beauty: [], other: [] };
    const visible = schema.categories.filter((c) => c.show_in_me_defaults !== false);
    const cards = visible.map((c) => ({
      id: c.category_id,
      label: c.display_label || c.label,
      description: `${c.display_label || c.label} preferences`,
      icon: Sparkles,                // fallback if iconUrl missing
      iconUrl: c.icon_url,           // Pass 4b - admin-driven category image
      accent: c.color_primary
        ? { backgroundColor: `${c.color_primary}1A`, color: c.color_primary }
        : null,
      colorPrimary: c.color_primary,
      fields: c.preference_fields || [],
    }));
    return {
      beauty: cards.filter((c) => BEAUTY_IDS.has(c.id)),
      other: cards.filter((c) => !BEAUTY_IDS.has(c.id)),
    };
  }, [schema]);

  if (loading) {
    return (
      <div className="space-y-6 pb-24" data-testid="defaults-tab-loading">
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !schema) {
    return (
      <div className="text-center py-12 text-sm text-gray-500" data-testid="defaults-tab-error">
        Couldn't load preferences schema. Please refresh.
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24" data-testid="defaults-tab">
      {/* Universal */}
      {universalCards.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-500" />
            Universal Defaults
            <span className="text-xs font-normal text-gray-500">(for events &amp; all experiences)</span>
          </h3>
          <div className="grid gap-3">
            {universalCards.map((card) => (
              <CategoryCard
                key={card.id}
                id={card.id}
                label={card.label}
                description={card.description}
                icon={card.icon}
                accent={card.accent}
                border={card.border}
                data={getData(card.id)}
                fields={card.fields}
                onClick={() => setEditing(card)}
                onClear={handleClear}
              />
            ))}
          </div>
        </div>
      )}

      {/* Beauty */}
      {categoryCards.beauty.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-pink-500" />
            Beauty Defaults
            <span className="text-xs font-normal text-gray-500">(for beauty appointments)</span>
          </h3>
          <div className="grid gap-3">
            {categoryCards.beauty.map((card) => (
              <CategoryCard
                key={card.id}
                id={card.id}
                label={card.label}
                description={card.description}
                icon={card.icon}
                iconUrl={card.iconUrl}
                accent={card.accent}
                data={getData(card.id)}
                fields={card.fields}
                onClick={() => setEditing(card)}
                onClear={handleClear}
              />
            ))}
          </div>
        </div>
      )}

      {/* Lifestyle / Other */}
      {categoryCards.other.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Plane className="w-4 h-4 text-sky-500" />
            Lifestyle &amp; Experience Defaults
            <span className="text-xs font-normal text-gray-500">(travel, wellness, events &amp; more)</span>
          </h3>
          <div className="grid gap-3">
            {categoryCards.other.map((card) => (
              <CategoryCard
                key={card.id}
                id={card.id}
                label={card.label}
                description={card.description}
                icon={card.icon}
                iconUrl={card.iconUrl}
                accent={card.accent}
                data={getData(card.id)}
                fields={card.fields}
                onClick={() => setEditing(card)}
                onClear={handleClear}
              />
            ))}
          </div>
        </div>
      )}

      {editing && (
        <CategoryEditModal
          id={editing.id}
          label={editing.label}
          icon={editing.icon}
          accent={editing.accent}
          fields={editing.fields}
          data={getData(editing.id)}
          onSave={handleSave}
          onClose={() => setEditing(null)}
          onClear={handleClear}
        />
      )}
    </div>
  );
};

export default DefaultsTab;
